import { useState, useEffect, useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Clock, LinkExternal01, AlertCircle, Minimize02, MarkerPin06, ChevronDown } from '@untitledui/icons';
import { Tooltip } from '@/components/base/tooltip/tooltip';
import { Badge } from '@/components/base/badges/badges';
import { useI18n } from '@/hooks/use-i18n';
import type { Container } from '@/types/container';

function getRiskLevelColor(riskLevel: string): 'gray' | 'success' | 'warning' | 'error' {
    switch (riskLevel) {
        case 'Low': return 'success';
        case 'Medium': return 'warning';
        case 'High': return 'error';
        case 'Critical': return 'error';
        default: return 'gray';
    }
}

function getStatusColor(status: string): 'gray' | 'brand' | 'warning' | 'success' | 'error' {
    switch (status) {
        case 'Draft': return 'gray';
        case 'Booked': return 'brand';
        case 'In Transit': return 'brand';
        case 'Arrived': return 'warning';
        case 'Delivered': return 'success';
        case 'On Hold': return 'warning';
        case 'Cancelled': return 'error';
        default: return 'gray';
    }
}

function getEtaLabel(eta?: Date, status?: string): string | null {
    if (!eta || status === 'Arrived' || status === 'Delivered') return null;
    const ms = eta.getTime() - Date.now();
    if (ms <= 0) return 'ETA 0H';
    const totalHours = Math.round(ms / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    return days > 0 ? `${days}D` : `${hours}H`;
}

function formatShortDate(date?: Date): string {
    if (!date) return '—';
    return date.toLocaleDateString(navigator.language, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

const SHIP_BOW_MARKER_SRC = `${import.meta.env.BASE_URL}images/ship-bow-marker.png`;

function CollapsibleSection({
    title,
    defaultOpen = false,
    children,
}: {
    title: string;
    defaultOpen?: boolean;
    children: ReactNode;
}) {
    const [open, setOpen] = useState(defaultOpen);
    const baseId = useId();
    const triggerId = `${baseId}-trigger`;
    const panelId = `${baseId}-panel`;

    return (
        <div className="border-b border-white/10">
            <button
                type="button"
                id={triggerId}
                aria-expanded={open}
                aria-controls={panelId}
                className="flex w-full items-center justify-between gap-2 px-6 py-2.5 text-left transition-colors hover:bg-white/[0.06]"
                onClick={() => setOpen((v) => !v)}
            >
                <span className="text-sm font-medium text-primary">{title}</span>
                <ChevronDown
                    className={`size-4 shrink-0 text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                />
            </button>
            {open ? (
                <div
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    className="border-t border-white/5 px-6 pb-3 pt-2"
                >
                    {children}
                </div>
            ) : null}
        </div>
    );
}

export interface ContainerGlassDetailProps {
    container: Container;
    lastUpdateTime?: Date | null;
    onClose: () => void;
    /** Nasconde la card per vedere la rotta; clic sul marker la riapre */
    onCollapse?: () => void;
    collapseTooltip?: string;
    formatDateTime?: (date: Date) => string;
    /** Conteggio active issues dall'API (stesso della pagina dettaglio). Se fornito, la sezione "Active issues" usa questo valore. */
    activeIssuesCount?: number;
}

export function ContainerGlassDetail({
    container,
    lastUpdateTime,
    onClose,
    onCollapse,
    collapseTooltip = 'Hide to see route',
    formatDateTime = (d) => new Intl.DateTimeFormat(navigator.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(d),
    activeIssuesCount: activeIssuesCountFromApi,
}: ContainerGlassDetailProps) {
    const { t } = useTranslation(['pages:containers', 'common']);
    const { translateContainerStatus, translateRiskLevel, translateDocumentStatus } = useI18n();
    const [timeAgo, setTimeAgo] = useState<string>('');

    useEffect(() => {
        if (!lastUpdateTime) {
            setTimeAgo('');
            return;
        }
        const updateTimeAgo = () => {
            const now = new Date();
            const diff = Math.floor((now.getTime() - lastUpdateTime.getTime()) / 1000);
            if (diff < 60) {
                setTimeAgo(`${diff} seconds ago`);
            } else if (diff < 3600) {
                const minutes = Math.floor(diff / 60);
                setTimeAgo(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`);
            } else {
                const hours = Math.floor(diff / 3600);
                setTimeAgo(`${hours} ${hours === 1 ? 'hour' : 'hours'} ago`);
            }
        };
        updateTimeAgo();
        const interval = setInterval(updateTimeAgo, 60000);
        return () => clearInterval(interval);
    }, [lastUpdateTime]);

    const cargoText =
        (container.cargoDescription && container.cargoDescription.trim()) ||
        (container.commodity && container.commodity !== '—' ? container.commodity : '');
    const showShipmentBlock =
        !!container.shippingCompany ||
        !!container.vesselName ||
        !!container.voyageNumber ||
        !!cargoText ||
        !!container.cargoType ||
        !!container.transportMode;

    const vesselVoyageLine = [container.vesselName, container.voyageNumber].filter(Boolean).join(' · ');
    const progressRatio = Math.max(0, Math.min(1, (container.progress ?? 0) / 100));
    const departureDate = container.actualDepartureDate ?? container.estimatedDepartureDate;
    const fromCode = container.originPortCode || '—';
    const toCode = container.destinationPortCode || '—';
    const etaLabel = getEtaLabel(container.eta, container.status);
    // Keep ship marker between left/right port badges.
    const shipPositionPct = Math.max(16, Math.min(84, progressRatio * 100));

    const cp = container.currentPosition;
    const aisSegments: string[] = [];
    if (cp?.speedKnots != null && Number.isFinite(cp.speedKnots)) {
        aisSegments.push(
            t('worldMap.aisSpeedKn', {
                value: cp.speedKnots.toFixed(1),
                defaultValue: '{{value}} kn',
            })
        );
    }
    if (cp?.imo) {
        aisSegments.push(t('worldMap.aisImo', { imo: cp.imo, defaultValue: 'IMO {{imo}}' }));
    }
    if (cp?.mmsi) {
        aisSegments.push(t('worldMap.aisMmsi', { mmsi: cp.mmsi, defaultValue: 'MMSI {{mmsi}}' }));
    }
    if (cp?.courseDegrees != null && Number.isFinite(cp.courseDegrees)) {
        aisSegments.push(
            t('worldMap.aisCourse', {
                deg: Math.round(cp.courseDegrees).toString(),
                defaultValue: '{{deg}}°',
            })
        );
    }
    if (cp?.headingDegrees != null && Number.isFinite(cp.headingDegrees)) {
        aisSegments.push(
            t('worldMap.aisHeading', {
                deg: Math.round(cp.headingDegrees).toString(),
                defaultValue: 'Hdg {{deg}}°',
            })
        );
    }
    const showAisLine = aisSegments.length > 0;

    const showPositionBlock =
        container.currentPosition != null &&
        Number.isFinite(container.currentPosition.lat) &&
        Number.isFinite(container.currentPosition.lng);

    const hasIssuesBanner =
        (activeIssuesCountFromApi != null && activeIssuesCountFromApi > 0) ||
        (activeIssuesCountFromApi == null &&
            (container.documentStatus !== 'Completo' ||
                container.hasActiveAlerts ||
                (container.alertCount != null && container.alertCount > 0)));

    return (
        <div
            className="flex max-h-[min(78vh,640px)] max-w-md w-full flex-col overflow-hidden rounded-xl bg-primary/85 shadow-xl ring-1 ring-white/10 backdrop-blur-md"
            role="dialog"
            aria-label="Container details"
        >
            {/* Header: titolo + azioni (compatto) */}
            <div className="flex-shrink-0 border-b border-white/10 px-6 py-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <h2 className="truncate text-xl font-semibold text-primary">
                            {container.containerNumber || '—'}
                        </h2>
                        <Link
                            to={`/shipper/containers/${container.id}`}
                            className="flex-shrink-0 rounded-lg p-1.5 text-tertiary transition-colors hover:bg-white/10 hover:text-primary"
                            aria-label="Open container details"
                        >
                            <LinkExternal01 className="size-5" />
                        </Link>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                        {onCollapse && (
                            <Tooltip title={collapseTooltip} placement="bottom">
                                <button
                                    type="button"
                                    onClick={onCollapse}
                                    className="rounded-lg p-1.5 text-tertiary transition-colors hover:bg-white/10 hover:text-primary"
                                    aria-label={collapseTooltip}
                                >
                                    <Minimize02 className="size-5" />
                                </button>
                            </Tooltip>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-1.5 text-tertiary transition-colors hover:bg-white/10 hover:text-primary"
                            aria-label="Close"
                        >
                            <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Stato, avvisi e tratta: blocco fisso sempre visibile (non collassabile) */}
            <div className="flex-shrink-0 border-b border-white/10">
                <div className="px-6 py-3">
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        <div>
                            <div className="mb-1 text-xs text-tertiary">Status</div>
                            <Badge color={getStatusColor(container.status)} size="sm">
                                {translateContainerStatus(container.status)}
                            </Badge>
                        </div>
                        <div>
                            <div className="mb-1 text-xs text-tertiary">Progress</div>
                            <div className="text-sm font-medium text-primary">{container.progress}%</div>
                        </div>
                        <div>
                            <div className="mb-1 text-xs text-tertiary">ETA</div>
                            <div className="text-sm text-primary">
                                {container.eta
                                    ? container.eta.toLocaleDateString(navigator.language, {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                    })
                                    : '—'}
                            </div>
                        </div>
                        <div>
                            <div className="mb-1 text-xs text-tertiary">Risk level</div>
                            <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                {translateRiskLevel(container.riskLevel)}
                            </Badge>
                        </div>
                    </div>
                    {hasIssuesBanner ? (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <AlertCircle className="size-4 flex-shrink-0 text-warning-500" />
                            <span className="text-xs text-tertiary">Active issues:</span>
                            {activeIssuesCountFromApi != null && activeIssuesCountFromApi > 0 ? (
                                <Badge color="warning" size="sm">
                                    {activeIssuesCountFromApi}{' '}
                                    {activeIssuesCountFromApi === 1 ? 'issue' : 'issues'}
                                </Badge>
                            ) : (
                                <>
                                    {container.documentStatus !== 'Completo' && (
                                        <Badge
                                            color={container.documentStatus === 'Error' ? 'error' : 'warning'}
                                            size="sm"
                                        >
                                            Docs: {translateDocumentStatus(container.documentStatus)}
                                        </Badge>
                                    )}
                                    {container.hasActiveAlerts && (
                                        <Badge color="warning" size="sm">
                                            {container.alertCount != null && container.alertCount > 0
                                                ? `${container.alertCount} alert(s)`
                                                : 'Alerts'}
                                        </Badge>
                                    )}
                                </>
                            )}
                        </div>
                    ) : null}
                </div>
                <div className="border-t border-white/10 px-6 py-3">
                    <div className="space-y-1.5">
                        <div className="relative isolate h-5">
                            <div
                                className="pointer-events-none absolute left-0 top-1/2 z-0 box-border h-0 -translate-y-1/2 border-t-2 border-dashed border-tertiary"
                                style={{ width: `${progressRatio * 100}%` }}
                            />
                            <div
                                className="pointer-events-none absolute top-1/2 z-0 box-border h-0 -translate-y-1/2"
                                style={{
                                    left: `${progressRatio * 100}%`,
                                    width: `${100 - progressRatio * 100}%`,
                                    borderTopWidth: 2,
                                    borderTopStyle: 'solid',
                                    borderTopColor: '#2563eb',
                                }}
                            />
                            <span className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-tertiary ring-1 ring-secondary_alt">
                                {fromCode}
                            </span>
                            <span className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-tertiary ring-1 ring-secondary_alt">
                                {toCode}
                            </span>
                            {etaLabel && (
                                <span
                                    className="absolute top-1/2 z-10 -translate-y-1/2 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-tertiary ring-1 ring-secondary_alt"
                                    style={{ left: `${Math.min(82, Math.max(50, progressRatio * 100 + 16))}%` }}
                                >
                                    {etaLabel}
                                </span>
                            )}
                            <span
                                className="absolute top-1/2 z-20 flex size-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                                style={{ left: `${shipPositionPct}%` }}
                                aria-hidden="true"
                            >
                                <img
                                    src={SHIP_BOW_MARKER_SRC}
                                    alt=""
                                    width={22}
                                    height={22}
                                    draggable={false}
                                    className="pointer-events-none select-none object-contain drop-shadow-sm"
                                    style={{
                                        filter:
                                            'invert(1) brightness(1.08) drop-shadow(0 0 1px rgba(15,23,42,0.9))',
                                    }}
                                />
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="min-w-0">
                                <div className="truncate text-tertiary">{container.route.from}</div>
                                <div className="text-quaternary">{formatShortDate(departureDate)}</div>
                            </div>
                            <div className="min-w-0 text-right">
                                <div className="truncate text-tertiary">{container.route.to}</div>
                                <div className="text-quaternary">{formatShortDate(container.eta)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {showShipmentBlock ? (
                    <CollapsibleSection
                        title={t('worldMap.sectionShipment', { defaultValue: 'Shipment details' })}
                        defaultOpen={false}
                    >
                        <div className="space-y-2">
                            {container.shippingCompany ? (
                                <div className="min-w-0">
                                    <div className="mb-0.5 text-xs text-tertiary">
                                        {t('worldMap.shipmentCarrier', { defaultValue: 'Carrier' })}
                                    </div>
                                    <div className="line-clamp-2 text-sm text-primary">{container.shippingCompany}</div>
                                </div>
                            ) : null}
                            {vesselVoyageLine ? (
                                <div className="min-w-0">
                                    <div className="mb-0.5 text-xs text-tertiary">
                                        {t('worldMap.shipmentVesselVoyage', { defaultValue: 'Vessel · Voyage' })}
                                    </div>
                                    <div className="truncate text-sm font-medium text-primary">{vesselVoyageLine}</div>
                                </div>
                            ) : null}
                            {cargoText ? (
                                <div className="min-w-0">
                                    <div className="mb-0.5 text-xs text-tertiary">
                                        {t('worldMap.shipmentCargo', { defaultValue: 'Cargo' })}
                                    </div>
                                    <div className="line-clamp-2 text-sm text-primary">{cargoText}</div>
                                </div>
                            ) : null}
                            {container.cargoType || container.transportMode ? (
                                <div className="flex flex-wrap gap-1.5">
                                    {container.cargoType ? (
                                        <Badge color="gray" size="sm">
                                            {container.cargoType}
                                        </Badge>
                                    ) : null}
                                    {container.transportMode ? (
                                        <Badge color="gray" size="sm">
                                            {container.transportMode}
                                        </Badge>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </CollapsibleSection>
                ) : null}

                {showPositionBlock && container.currentPosition ? (
                    <CollapsibleSection
                        title={t('worldMap.sectionPosition', { defaultValue: 'Position & AIS' })}
                        defaultOpen={false}
                    >
                        <div className="flex items-start gap-2">
                            <MarkerPin06 className="mt-0.5 size-4 flex-shrink-0 text-tertiary" aria-hidden />
                            <div className="min-w-0 flex-1">
                                <div className="mb-1 text-xs text-tertiary">
                                    {t('worldMap.lastKnownCoordinates', {
                                        defaultValue: 'Last known coordinates',
                                    })}
                                </div>
                                <div className="break-all font-mono text-sm tabular-nums text-primary">
                                    {container.currentPosition.lat.toFixed(5)}, {container.currentPosition.lng.toFixed(5)}
                                </div>
                                {container.currentPosition.recordedAt ? (
                                    <div className="mt-1 text-xs text-tertiary">
                                        {t('worldMap.positionRecordedAt', {
                                            time: formatDateTime(container.currentPosition.recordedAt),
                                            defaultValue: 'Recorded: {{time}}',
                                        })}
                                    </div>
                                ) : null}
                                {container.currentPosition.positionFixAt ? (
                                    <div className="mt-0.5 text-xs text-tertiary">
                                        {t('worldMap.positionFixAt', {
                                            time: formatDateTime(container.currentPosition.positionFixAt),
                                            defaultValue: 'Position fix: {{time}}',
                                        })}
                                    </div>
                                ) : null}
                                {showAisLine ? (
                                    <div className="mt-2 border-t border-white/10 pt-2 text-xs text-tertiary">
                                        <span className="font-medium text-tertiary">
                                            {t('worldMap.aisLabel', { defaultValue: 'AIS' })}
                                        </span>
                                        <span className="text-primary/90"> · {aisSegments.join(' · ')}</span>
                                    </div>
                                ) : null}
                                {cp?.aisDestination ? (
                                    <div className="mt-1 text-xs text-tertiary">
                                        {t('worldMap.aisDestination', {
                                            dest: cp.aisDestination,
                                            defaultValue: 'AIS dest.: {{dest}}',
                                        })}
                                    </div>
                                ) : null}
                                {cp?.aisEtaUtc ? (
                                    <div className="text-xs text-tertiary">
                                        {t('worldMap.aisEta', {
                                            eta: cp.aisEtaUtc,
                                            defaultValue: 'AIS ETA: {{eta}}',
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </CollapsibleSection>
                ) : null}
            </div>

            {/* Footer: Received X ago */}
            <div className="flex flex-shrink-0 items-center gap-2 border-t border-white/10 px-6 py-2.5 text-xs text-tertiary">
                <Clock className="size-4 flex-shrink-0" />
                <span>
                    {timeAgo ? `Received: ${timeAgo}` : lastUpdateTime ? formatDateTime(lastUpdateTime) : 'No tracking data'}
                </span>
            </div>
        </div>
    );
}
